const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const clearTimers = new WeakMap();
const clearAnimations = new WeakMap();

const clamp = (value) => Math.min(1, Math.max(0, value));

const readNumber = (styles, property, fallback) => {
  const value = Number.parseFloat(styles.getPropertyValue(property));

  return Number.isFinite(value) ? value : fallback;
};

const readBezier = (styles, property) => {
  const values = styles
    .getPropertyValue(property)
    .match(/-?[\d.]+/g)
    ?.map(Number);

  return values?.length === 4 ? values : [0.22, 1, 0.36, 1];
};

const readClearConfig = () => {
  const styles = getComputedStyle(document.documentElement);

  return {
    duration: readNumber(styles, '--clear-dur', 280),
    outDuration: readNumber(styles, '--clear-out-dur', 180),
    inDuration: readNumber(styles, '--clear-in-dur', 220),
    outFly: readNumber(styles, '--clear-out-fly', 6),
    inFly: readNumber(styles, '--clear-in-fly', 6),
    outEase: readBezier(styles, '--clear-out-ease'),
    inEase: readBezier(styles, '--clear-in-ease'),
    blur: readNumber(styles, '--clear-blur', 1),
    glowDelay: readNumber(styles, '--glow-delay', 0),
    glowPeakAt: readNumber(styles, '--glow-peak-at', 0.22),
    glowOpacity: readNumber(styles, '--glow-opacity', 0.16),
    glowSpread: readNumber(styles, '--glow-spread', 1.05),
  };
};

const isSearchField = (field) => field instanceof HTMLInputElement && field.type === 'search';

const getSearchLayers = (container) => ({
  mirror: container.querySelector('[data-input-clear-mirror]'),
  placeholder: container.querySelector('[data-input-clear-placeholder]'),
  glow: container.querySelector('[data-input-clear-glow]'),
});

const copyInputTypography = (field, layer) => {
  const styles = getComputedStyle(field);

  layer.style.font = styles.font;
  layer.style.letterSpacing = styles.letterSpacing;
  layer.style.lineHeight = styles.lineHeight;
  layer.style.textTransform = styles.textTransform;
  layer.style.textIndent = styles.textIndent;
  layer.style.textAlign = styles.textAlign;
  layer.style.padding = styles.padding;
  layer.style.borderStyle = 'solid';
  layer.style.borderWidth = `${styles.borderTopWidth} ${styles.borderRightWidth} ${styles.borderBottomWidth} ${styles.borderLeftWidth}`;
  layer.style.borderColor = 'transparent';
  layer.style.color = styles.color;
};

const copyPlaceholderTypography = (field, placeholder) => {
  copyInputTypography(field, placeholder);
  placeholder.style.color = getComputedStyle(field, '::placeholder').color;
};

const positionSearchLayers = (container, field) => {
  const containerBounds = container.getBoundingClientRect();
  const fieldBounds = field.getBoundingClientRect();
  const { mirror, placeholder, glow } = getSearchLayers(container);

  if (!mirror || !placeholder || !glow || fieldBounds.width === 0 || fieldBounds.height === 0) return;

  container.style.setProperty('--input-clear-layer-top', `${fieldBounds.top - containerBounds.top}px`);
  container.style.setProperty('--input-clear-layer-left', `${fieldBounds.left - containerBounds.left}px`);
  container.style.setProperty('--input-clear-layer-width', `${fieldBounds.width}px`);
  container.style.setProperty('--input-clear-layer-height', `${fieldBounds.height}px`);
  container.style.setProperty('--input-clear-mirror-scroll-x', `${-field.scrollLeft}px`);

  copyInputTypography(field, mirror);
  copyPlaceholderTypography(field, placeholder);
};

const renderMirrorValue = (mirror, value) => {
  const fragment = document.createDocumentFragment();

  value.split(/(\s+)/).forEach((part) => {
    if (!part) return;

    if (/^\s+$/.test(part)) {
      fragment.append(part);
      return;
    }

    const word = document.createElement('span');
    word.className = 'input-clear__mirror-word';
    word.dataset.inputClearMirrorWord = '';
    word.textContent = part;
    fragment.append(word);
  });

  mirror.replaceChildren(fragment);
};

const syncSearchLayers = (container, field) => {
  const { mirror } = getSearchLayers(container);

  if (!mirror) return;

  positionSearchLayers(container, field);
  renderMirrorValue(mirror, field.value);
};

const getGlowWords = (container, mirror) => {
  const containerBounds = container.getBoundingClientRect();

  return Array.from(mirror.querySelectorAll('[data-input-clear-mirror-word]')).map((word) => {
    const bounds = word.getBoundingClientRect();

    return {
      x: bounds.left - containerBounds.left + bounds.width / 2,
      y: bounds.top - containerBounds.top + bounds.height / 2,
      width: bounds.width,
      height: bounds.height,
    };
  });
};

const resetSearchClear = (container) => {
  const animation = clearAnimations.get(container);

  if (animation) {
    window.cancelAnimationFrame(animation.frame);
    animation.textAnimations.forEach((textAnimation) => textAnimation.cancel());
    clearAnimations.delete(container);
  }

  const { mirror, placeholder, glow } = getSearchLayers(container);

  container.classList.remove('is-clearing', 'is-placeholder-entering', 'is-placeholder-resting');
  mirror?.replaceChildren();
  mirror?.style.removeProperty('opacity');
  mirror?.style.removeProperty('transform');
  mirror?.style.removeProperty('filter');
  placeholder?.replaceChildren();
  placeholder?.style.removeProperty('opacity');
  placeholder?.style.removeProperty('transform');
  placeholder?.style.removeProperty('filter');
  glow?.style.removeProperty('opacity');
  glow?.style.removeProperty('background-image');
};

const finishSearchClear = (container) => {
  const animation = clearAnimations.get(container);

  if (animation) {
    window.cancelAnimationFrame(animation.frame);
    animation.textAnimations.forEach((textAnimation) => textAnimation.cancel());
    clearAnimations.delete(container);
  }

  const { mirror, glow } = getSearchLayers(container);

  container.classList.remove('is-clearing');
  mirror?.replaceChildren();
  mirror?.style.removeProperty('opacity');
  mirror?.style.removeProperty('transform');
  mirror?.style.removeProperty('filter');
  glow?.style.removeProperty('opacity');
  glow?.style.removeProperty('background-image');
};

const updateClearButton = (container) => {
  const field = container.querySelector('[data-input-clear-field]');
  const button = container.querySelector('[data-input-clear-button]');

  if (!field || !button) return;

  const hasValue = field.value.length > 0;
  container.dataset.hasValue = String(hasValue);
  container.classList.toggle('has-value', hasValue);
  button.disabled = !hasValue;
  button.setAttribute('aria-hidden', String(!hasValue));
};

const emitInput = (field) => {
  field.dispatchEvent(new Event('input', { bubbles: true }));
};

const clearFieldImmediately = (container, field) => {
  field.value = '';
  emitInput(field);
  updateClearButton(container);
  field.dispatchEvent(new Event('change', { bubbles: true }));
};

const clearNonSearchField = (container, field) => {
  if (!field.value) return;

  const currentTimer = clearTimers.get(field);
  if (currentTimer) window.clearInterval(currentTimer);
  clearTimers.delete(field);

  if (reduceMotion.matches) {
    clearFieldImmediately(container, field);
    return;
  }

  let remaining = field.value;
  const interval = Math.max(6, Math.min(14, 100 / remaining.length));
  const timer = window.setInterval(() => {
    remaining = remaining.slice(0, -1);
    field.value = remaining;
    emitInput(field);
    updateClearButton(container);

    if (remaining.length === 0) {
      window.clearInterval(timer);
      clearTimers.delete(field);
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, interval);

  clearTimers.set(field, timer);
};

const clearSearchField = (container, field) => {
  if (!field.value) return;

  resetSearchClear(container);

  if (reduceMotion.matches) {
    clearFieldImmediately(container, field);
    return;
  }

  syncSearchLayers(container, field);

  const { mirror, placeholder, glow } = getSearchLayers(container);
  if (!mirror || !placeholder || !glow) {
    clearFieldImmediately(container, field);
    return;
  }

  const config = readClearConfig();
  const glowWords = getGlowWords(container, mirror);
  const mirrorScrollX = -field.scrollLeft;
  const startedAt = performance.now();
  const animationId = Symbol('input-clear');

  placeholder.textContent = field.placeholder;
  container.classList.add('is-clearing', 'is-placeholder-entering');
  field.value = '';
  emitInput(field);
  updateClearButton(container);
  field.dispatchEvent(new Event('change', { bubbles: true }));

  const toEasing = (values) => `cubic-bezier(${values.join(', ')})`;
  const mirrorAnimation = mirror.animate(
    [
      { opacity: 1, transform: `translate3d(${mirrorScrollX}px, 0, 0)`, filter: 'blur(0)' },
      {
        opacity: 0,
        transform: `translate3d(${mirrorScrollX}px, ${config.outFly}px, 0)`,
        filter: `blur(${config.blur}px)`,
      },
    ],
    { duration: config.outDuration, easing: toEasing(config.outEase), fill: 'forwards' },
  );
  const placeholderAnimation = placeholder.animate(
    [
      { opacity: 0, transform: `translate3d(0, ${-config.inFly}px, 0)`, filter: `blur(${config.blur}px)` },
      { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0)' },
    ],
    { duration: config.inDuration, easing: toEasing(config.inEase), fill: 'forwards' },
  );
  const animationState = {
    id: animationId,
    frame: 0,
    textAnimations: [mirrorAnimation, placeholderAnimation],
  };

  clearAnimations.set(container, animationState);

  placeholderAnimation.finished
    .then(() => {
      if (clearAnimations.get(container)?.id !== animationId) return;

      container.classList.remove('is-placeholder-entering');
      container.classList.add('is-placeholder-resting');
      placeholder.style.opacity = '1';
      placeholder.style.transform = 'translate3d(0, 0, 0)';
      placeholder.style.filter = 'blur(0)';
      placeholderAnimation.cancel();
    })
    .catch(() => {});

  const renderFrame = (now) => {
    const elapsed = now - startedAt;
    const glowProgress = clamp((elapsed - config.glowDelay) / Math.max(1, config.duration - config.glowDelay));
    const glowEnvelope =
      glowProgress <= config.glowPeakAt
        ? glowProgress / Math.max(config.glowPeakAt, 0.01)
        : (1 - glowProgress) / Math.max(1 - config.glowPeakAt, 0.01);

    const glowStrength = clamp(glowEnvelope) * config.glowOpacity;
    glow.style.opacity = String(glowStrength);
    glow.style.backgroundImage = glowWords
      .map((word) => {
        const radiusX = Math.max(word.width * config.glowSpread, 12);
        const radiusY = Math.max(word.height * config.glowSpread, 12);
        const y = word.y + config.outFly * glowProgress;
        const alpha = Math.round(glowStrength * 100);

        return `radial-gradient(ellipse ${radiusX}px ${radiusY}px at ${word.x}px ${y}px, color-mix(in oklch, var(--sc-primary-text) ${alpha}%, transparent) 0%, transparent 72%)`;
      })
      .join(', ');

    if (elapsed < config.duration) {
      animationState.frame = window.requestAnimationFrame(renderFrame);
      return;
    }

    finishSearchClear(container);
  };

  animationState.frame = window.requestAnimationFrame(renderFrame);
};

const enhanceClearControl = (container) => {
  if (container.dataset.inputClearReady === 'true') return;

  const field = container.querySelector('[data-input-clear-field]');
  const button = container.querySelector('[data-input-clear-button]');

  if (!field || !button) return;

  container.dataset.inputClearReady = 'true';
  if (isSearchField(field)) {
    container.classList.add('input-clear--search-dissolve');

    const mirror = document.createElement('div');
    mirror.className = 'input-clear__mirror';
    mirror.setAttribute('aria-hidden', 'true');
    mirror.dataset.inputClearMirror = '';

    const placeholder = document.createElement('div');
    placeholder.className = 'input-clear__placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.dataset.inputClearPlaceholder = '';

    const glow = document.createElement('div');
    glow.className = 'input-clear__glow';
    glow.setAttribute('aria-hidden', 'true');
    glow.dataset.inputClearGlow = '';

    container.append(mirror, placeholder, glow);
    positionSearchLayers(container, field);

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => positionSearchLayers(container, field));
      resizeObserver.observe(container);
      resizeObserver.observe(field);
    }

    const placeholderObserver = new MutationObserver(() => {
      if (!container.matches('.is-placeholder-entering, .is-placeholder-resting')) return;

      placeholder.textContent = field.placeholder;
      copyPlaceholderTypography(field, placeholder);
    });
    placeholderObserver.observe(field, { attributes: true, attributeFilter: ['placeholder'] });
  }
  updateClearButton(container);

  field.addEventListener('input', (event) => {
    if (event.isTrusted) {
      const timer = clearTimers.get(field);
      if (timer) window.clearInterval(timer);
      clearTimers.delete(field);
    }

    if (isSearchField(field)) {
      if (container.classList.contains('is-placeholder-resting')) {
        resetSearchClear(container);
      } else if (container.classList.contains('is-clearing')) {
        if (event.isTrusted) resetSearchClear(container);
        else return;
      }

      positionSearchLayers(container, field);
    }

    updateClearButton(container);
  });

  if (isSearchField(field)) {
    field.addEventListener('scroll', () => {
      if (!container.classList.contains('is-clearing')) positionSearchLayers(container, field);
    });
  }

  button.addEventListener('mousedown', (event) => event.preventDefault());
  button.addEventListener('click', () => {
    if (isSearchField(field)) {
      clearSearchField(container, field);
    } else {
      clearNonSearchField(container, field);
    }

    field.focus({ preventScroll: true });
  });
};

const enhanceClearControls = (root = document) => {
  root.querySelectorAll('[data-input-clear]').forEach(enhanceClearControl);
};

enhanceClearControls();
document.addEventListener('shopify:section:load', (event) => enhanceClearControls(event.target));
